import { ArrowUp, Square, WifiOff } from "lucide-react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupTextarea,
} from "@opencloud/ui";
import { useEffect, useRef } from "react";
import { agentBrand } from "../brand.js";
import type { ChatUploads } from "../types.js";
import { AttachFilesButton, DraftAttachments } from "./ChatAttachments.js";
import { ComposerRuntimeControls, ComposerRuntimeProblemAlert, type ComposerRuntime } from "./ComposerRuntime.js";

export function Composer({
  draft,
  setDraft,
  runActive,
  offline,
  busy,
  stopping = false,
  canSend = true,
  canStop = true,
  label = "Message your app agent",
  placeholder = "Tell the agent what you need…",
  onSend,
  onStop,
  uploads,
  runtime,
}: {
  draft: string;
  setDraft: (value: string) => void;
  runActive: boolean;
  offline: boolean;
  busy: boolean;
  stopping?: boolean;
  canSend?: boolean;
  canStop?: boolean;
  label?: string;
  placeholder?: string;
  onSend: () => void;
  onStop: () => void;
  uploads?: ChatUploads | undefined;
  /** The owner's model and reasoning picker; omitted when the chat can't change them. */
  runtime?: ComposerRuntime | undefined;
}) {
  const input = useRef<HTMLTextAreaElement>(null);
  const hasInput = draft.trim().length > 0 || Boolean(uploads?.ready.length);
  const showStop = runActive && !hasInput && !uploads?.items.length;
  const actionUnavailable =
    offline ||
    stopping ||
    (showStop ? !canStop : !canSend) ||
    (!showStop && (busy || !hasInput || Boolean(uploads?.pending)));

  useEffect(() => {
    const element = input.current;
    if (!element) return;
    element.style.height = "auto";
    element.style.height = `${Math.min(element.scrollHeight, 180)}px`;
  }, [draft]);

  function submit() {
    if (offline || stopping || (showStop ? !canStop : !canSend)) return;
    if (showStop) {
      onStop();
      return;
    }
    if (!busy && hasInput && !uploads?.pending) onSend();
  }

  return (
    <div className="a3-composer-region" onDragOver={event=>{
      if(event.dataTransfer.types.includes("Files"))event.preventDefault();
    }} onDrop={event=>{
      if(!event.dataTransfer.files.length)return;
      event.preventDefault();
      if(canSend && !offline && !busy && uploads)void uploads.add(Array.from(event.dataTransfer.files));
    }} onPaste={event=>{
      if(!event.clipboardData.files.length || !uploads)return;
      event.preventDefault();
      if(canSend && !offline && !busy && !stopping)void uploads.add(Array.from(event.clipboardData.files));
    }}>
      {uploads ? <DraftAttachments uploads={uploads}/>:null}
      {offline ? (
        <Alert className="a3-offline-note">
          <WifiOff aria-hidden="true" />
          <AlertTitle>You’re offline</AlertTitle>
          <AlertDescription>
            {`Connection interrupted. Drafts and unsent messages stay here while ${agentBrand().productName} reconnects.`}
          </AlertDescription>
        </Alert>
      ) : null}
      {runtime?.problem && canSend ? <ComposerRuntimeProblemAlert problem={runtime.problem} /> : null}
      <InputGroup className="a3-composer has-disabled:opacity-100">
        <label className="sr-only" htmlFor="agent-composer-input">
          {label}
        </label>
        <InputGroupTextarea
          ref={input}
          id="agent-composer-input"
          className="a3-composer-input"
          rows={1}
          value={draft}
          placeholder={placeholder}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
              event.preventDefault();
              submit();
            }
          }}
        />
        <InputGroupAddon align="block-end" className="a3-composer-toolbar" data-runtime={runtime && canSend ? "true" : undefined}>
          {uploads || (runtime && canSend) ? <div className="a3-composer-tools">
            {uploads ? <AttachFilesButton uploads={uploads} disabled={!canSend || offline || busy}/>:null}
            {runtime && canSend ? <ComposerRuntimeControls runtime={runtime}/> : null}
          </div> : null}
          <span className="a3-composer-hint">
            Enter to send · Shift + Enter for a new line
          </span>
          {runActive && !showStop && <InputGroupButton
            type="button"
            size="sm"
            variant="outline"
            aria-label="Stop"
            aria-busy={stopping}
            aria-disabled={offline || stopping || !canStop}
            onClick={() => { if (!offline && !stopping && canStop) onStop(); }}
          ><Square aria-hidden="true" /><span>Stop</span></InputGroupButton>}
          <InputGroupButton
            type="button"
            className="a3-composer-primary"
            size={showStop ? "sm" : "icon-sm"}
            variant={showStop ? "destructive" : "default"}
            data-mode={showStop ? "stop" : "send"}
            aria-label={showStop ? "Stop" : "Send"}
            aria-busy={showStop ? stopping : busy}
            aria-disabled={actionUnavailable}
            onClick={submit}
          >
            {showStop ? (
              <Square aria-hidden="true" />
            ) : (
              <ArrowUp aria-hidden="true" />
            )}
            {showStop ? (
              <span className="a3-composer-primary-label">Stop</span>
            ) : null}
          </InputGroupButton>
        </InputGroupAddon>
      </InputGroup>
    </div>
  );
}
